import { useSearchParams } from 'react-router-dom';
import type { AdminStore } from '@/client/types/stores';
import { OrgRelationshipLink, UserRelationshipLink } from '@/client/components/admin/Payment/RelationshipLink';
import { Skeleton } from '@/components/ui/skeleton';

/** GHS currency formatter shared across store tabs. */
export const money = (n?: number | null): string =>
  n == null
    ? '—'
    : new Intl.NumberFormat('en-GH', {
        style: 'currency',
        currency: 'GHS',
        maximumFractionDigits: 2,
      }).format(n);

/**
 * A titled card. Leave `title` off when the section *is* the page (the Store
 * Manager content card already shows the page title); `bare` then drops the
 * card chrome so the toolbar sits directly in the content card.
 */
export const SectionCard: React.FC<{
  title?: string;
  description?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  bare?: boolean;
}> = ({ title, description, children, actions, bare }) => (
  <section className={bare ? undefined : 'rounded-lg border border-gray-200 bg-white p-5'}>
    {(title || description || actions) && (
      <div className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div>
          {title && <h3 className="text-sm font-semibold text-gray-900">{title}</h3>}
          {description && <p className={title ? 'mt-0.5 text-xs text-gray-500' : 'text-sm text-gray-500'}>{description}</p>}
        </div>
        {actions}
      </div>
    )}
    {children}
  </section>
);

export const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="text-right font-medium text-gray-900">{children}</span>
  </div>
);

// ── Loading skeletons ───────────────────────────────────────────────────────
// Each mirrors the layout it stands in for, so nothing jumps when data lands.

const WIDTHS = ['w-3/4', 'w-1/2', 'w-2/3', 'w-2/5', 'w-3/5'];

/** A table: header row + `rows` body rows of `cols` cells (first column wider). */
export const TableSkeleton: React.FC<{ rows?: number; cols?: number }> = ({ rows = 6, cols = 5 }) => (
  <div className="overflow-hidden" aria-busy="true" aria-label="Loading">
    <div className="flex gap-4 border-b border-gray-200 py-2.5">
      {Array.from({ length: cols }, (_, c) => (
        <Skeleton key={c} className={`h-3 ${c === 0 ? 'flex-[2]' : 'flex-1'}`} />
      ))}
    </div>
    {Array.from({ length: rows }, (_, r) => (
      <div key={r} className="flex items-center gap-4 border-b border-gray-100 py-3">
        {Array.from({ length: cols }, (_, c) => (
          <div key={c} className={c === 0 ? 'flex-[2]' : 'flex-1'}>
            <Skeleton className={`h-4 ${c === 0 ? WIDTHS[r % WIDTHS.length] : 'w-2/3'}`} />
          </div>
        ))}
      </div>
    ))}
  </div>
);

/** A divided list: optional thumbnail/avatar, two text lines, trailing pill. */
export const ListSkeleton: React.FC<{ rows?: number; media?: 'thumb' | 'avatar' | 'none'; trailing?: boolean }> = ({
  rows = 5,
  media = 'none',
  trailing = true,
}) => (
  <div className="divide-y divide-gray-100" aria-busy="true" aria-label="Loading">
    {Array.from({ length: rows }, (_, r) => (
      <div key={r} className="flex items-center gap-3 py-3">
        {media === 'thumb' && <Skeleton className="h-12 w-12 flex-shrink-0 rounded-md" />}
        {media === 'avatar' && <Skeleton className="h-9 w-9 flex-shrink-0 rounded-full" />}
        <div className="min-w-0 flex-1 space-y-1.5">
          <Skeleton className={`h-4 ${WIDTHS[r % WIDTHS.length]}`} />
          <Skeleton className="h-3 w-1/3" />
        </div>
        {trailing && <Skeleton className="h-5 w-16 rounded-full" />}
      </div>
    ))}
  </div>
);

/** Label/value rows, as rendered by `InfoRow`. */
export const InfoRowsSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div className="divide-y divide-gray-100" aria-busy="true" aria-label="Loading">
    {Array.from({ length: rows }, (_, r) => (
      <div key={r} className="flex justify-between gap-4 py-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className={`h-4 ${r % 2 ? 'w-20' : 'w-32'}`} />
      </div>
    ))}
  </div>
);

/** Form fields: label + input. */
export const FormSkeleton: React.FC<{ fields?: number; columns?: 1 | 2 }> = ({ fields = 4, columns = 2 }) => (
  <div className={`grid gap-4 ${columns === 2 ? 'sm:grid-cols-2' : ''}`} aria-busy="true" aria-label="Loading">
    {Array.from({ length: fields }, (_, i) => (
      <div key={i} className="space-y-1.5">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-9 w-full rounded-md" />
      </div>
    ))}
  </div>
);

/** Stat tiles (`StatTile` and friends). */
export const StatGridSkeleton: React.FC<{ count?: number; className?: string }> = ({ count = 3, className = 'grid-cols-3' }) => (
  <div className={`grid gap-3 ${className}`} aria-busy="true" aria-label="Loading">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="rounded-lg border border-gray-200 bg-white p-4">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="mt-2 h-6 w-24" />
      </div>
    ))}
  </div>
);

/** A `SectionCard` whose body is still loading. */
export const CardSkeleton: React.FC<{ children?: React.ReactNode; description?: boolean }> = ({ children, description }) => (
  <section className="rounded-lg border border-gray-200 bg-white p-5" aria-busy="true" aria-label="Loading">
    <div className="mb-4 space-y-1.5">
      <Skeleton className="h-4 w-32" />
      {description && <Skeleton className="h-3 w-56" />}
    </div>
    {children ?? <InfoRowsSkeleton />}
  </section>
);

/** Toolbar row of a page-level section: description left, controls right. */
export const ToolbarSkeleton: React.FC<{ controls?: number }> = ({ controls = 2 }) => (
  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
    <Skeleton className="h-4 w-64" />
    <div className="flex gap-2">
      {Array.from({ length: controls }, (_, i) => <Skeleton key={i} className="h-8 w-24 rounded-md" />)}
    </div>
  </div>
);

/** A vertical timeline (audit / moderation history). */
export const TimelineSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <ol className="relative space-y-4 border-l border-gray-200 pl-5" aria-busy="true" aria-label="Loading">
    {Array.from({ length: rows }, (_, r) => (
      <li key={r} className="relative">
        <span className="absolute -left-[23px] top-1 h-2.5 w-2.5 rounded-full bg-gray-200" />
        <div className="flex items-center justify-between gap-4">
          <Skeleton className={`h-4 ${WIDTHS[r % WIDTHS.length]}`} />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="mt-1.5 h-3 w-1/3" />
      </li>
    ))}
  </ol>
);


/** Prev/next pager for offset-paginated store lists. */
export const Pager: React.FC<{
  total: number;
  limit: number;
  offset: number;
  onChange: (offset: number) => void;
}> = ({ total, limit, offset, onChange }) => {
  if (total <= limit) return null;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  return (
    <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3 text-xs text-gray-500">
      <span>
        {from}–{to} of {total}
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => onChange(Math.max(0, offset - limit))}
          className="rounded-md border border-gray-200 px-2.5 py-1 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          Previous
        </button>
        <button
          type="button"
          disabled={offset + limit >= total}
          onClick={() => onChange(offset + limit)}
          className="rounded-md border border-gray-200 px-2.5 py-1 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
};

/** Short date for table cells. */
export const shortDate = (raw?: string | null): string => {
  if (!raw) return '—';
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? raw
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** A 0/1/boolean flag from an ORM passthrough row. */
export const isOn = (v: unknown): boolean => v === 1 || v === true || v === '1';

export const Pill: React.FC<{ tone: string; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${tone}`}>
    {children}
  </span>
);

export const ORDER_STATUS_TONE: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  canceled: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-gray-100 text-gray-600',
  refunded: 'bg-red-100 text-red-800',
};

// ── Date ranges ─────────────────────────────────────────────────────────────

export const RANGE_PRESETS = [
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
  { key: '365d', label: '12 months', days: 365 },
] as const;
export type RangeKey = (typeof RANGE_PRESETS)[number]['key'];

const isoDay = (d: Date): string => d.toISOString().slice(0, 10);

/** `{date_from, date_to}` (inclusive, YYYY-MM-DD) for a preset ending today. */
export const rangeFor = (key: RangeKey): { date_from: string; date_to: string } => {
  const days = RANGE_PRESETS.find((r) => r.key === key)?.days ?? 30;
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - (days - 1));
  return { date_from: isoDay(from), date_to: isoDay(to) };
};

/** Bucket that keeps a chart readable for the range. */
export const bucketFor = (key: RangeKey): 'day' | 'week' | 'month' =>
  key === '365d' ? 'month' : key === '90d' ? 'week' : 'day';

export const RangePicker: React.FC<{ value: RangeKey; onChange: (k: RangeKey) => void }> = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-1.5 text-xs">
    {RANGE_PRESETS.map((r) => (
      <button
        key={r.key}
        type="button"
        onClick={() => onChange(r.key)}
        aria-pressed={value === r.key}
        className={`rounded-full px-3 py-1.5 font-medium transition-colors ${
          value === r.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        {r.label}
      </button>
    ))}
  </div>
);

/** % change vs the previous period, or null when there is no baseline. */
export const pctChange = (current: number, previous: number): number | null =>
  previous ? ((current - previous) / previous) * 100 : null;

export const StatTile: React.FC<{
  label: string;
  value: string;
  icon?: React.ReactNode;
  sub?: string;
  delta?: number | null;
}> = ({ label, value, icon, sub, delta }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4">
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</span>
      {icon}
    </div>
    <p className="mt-1 text-xl font-bold text-gray-900">{value}</p>
    <div className="mt-0.5 flex items-center gap-2 text-xs">
      {delta != null && (
        <span className={delta >= 0 ? 'font-medium text-emerald-600' : 'font-medium text-red-600'}>
          {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
        </span>
      )}
      {sub && <span className="text-gray-500">{sub}</span>}
    </div>
  </div>
);

/** Tone for any store workflow status (orders, refunds, returns, invoices…). */
export const statusTone = (status?: string | null): string => {
  const s = String(status ?? '').toLowerCase();
  if (['delivered', 'completed', 'approved', 'paid', 'received', 'processed', 'successful', 'active', 'live'].includes(s))
    return 'bg-green-100 text-green-800';
  if (['cancelled', 'canceled', 'rejected', 'failed', 'voided', 'revoked', 'return_rejected', 'expired'].includes(s))
    return 'bg-gray-100 text-gray-600';
  if (['refunded', 'refund_requested', 'return_requested', 'overdue'].includes(s)) return 'bg-red-100 text-red-800';
  if (['shipped', 'processing', 'confirmed', 'return_approved', 'returned'].includes(s)) return 'bg-indigo-100 text-indigo-800';
  return 'bg-amber-100 text-amber-800';
};

export const label = (s?: string | null): string => (s ? s.replace(/_/g, ' ') : '—');

/** The selected range lives in the URL (`?range=90d`) next to `?tab=`, so a
 * shared link opens the same view. */
export const useRangeParam = (fallback: RangeKey = '30d'): [RangeKey, (k: RangeKey) => void] => {
  const [params, setParams] = useSearchParams();
  const raw = params.get('range');
  const value = (RANGE_PRESETS.some((r) => r.key === raw) ? raw : fallback) as RangeKey;
  const set = (k: RangeKey) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('range', k);
        return next;
      },
      { replace: true },
    );
  return [value, set];
};

/**
 * The store's owner by name (from the `owner` summary the admin API returns),
 * linked to the user or organization. Falls back to the id while unknown.
 */
export const StoreOwnerLink: React.FC<{ store: AdminStore }> = ({ store }) => {
  const owner = store.owner;
  if (store.oid != null) {
    return <OrgRelationshipLink oid={store.oid} summary={{ oid: store.oid, name: owner?.name ?? null }} />;
  }
  return (
    <UserRelationshipLink
      uid={store.uid}
      summary={store.uid != null ? { uid: store.uid, name: owner?.name ?? null, email: owner?.email ?? null } : null}
    />
  );
};
